import { Link } from 'react-router-dom';
import type { ListingSearchItem } from './queries';
import { formatAmount } from '@/lib/money';
import { ROUTES } from '@/config/navigation';
import { FUEL_LABELS, TRANSMISSION_LABELS } from '@/config/platform';
import { LISTING_CONDITION_LABELS } from '@/types/domain';
import type { FuelType, Transmission, ListingCondition } from '@/types/domain';

/**
 * Carte d'une annonce de vente.
 *
 * Elle vivait à l'intérieur du catalogue. L'accueil devant afficher les mêmes
 * annonces, la sortir ici évite d'en tenir deux versions : une carte dupliquée
 * finit toujours par diverger, et c'est la copie oubliée qui reste en ligne.
 *
 * L'action s'appelle « Voir », pas « Acheter » : la plateforme ne vend rien et
 * ne transporte aucun argent.
 */
export function ListingCard({ item }: { item: ListingSearchItem }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-card transition-shadow hover:shadow-lift">
      <Link to={ROUTES.listing(item.id)} className="block aspect-[4/3] overflow-hidden bg-ink-100">
        {item.photo ? (
          <img
            src={item.photo}
            alt={`${item.brand} ${item.model}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <span className="flex h-full items-center justify-center text-sm text-ink-400">
            Photo à venir
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-[15px] font-bold leading-snug text-ink-950">
          <Link to={ROUTES.listing(item.id)} className="hover:underline">
            {item.brand} {item.model}
          </Link>
        </h3>
        <p className="mt-1 text-xs text-ink-500">
          {item.year} · {item.mileage_km.toLocaleString('fr-FR')} km ·{' '}
          {LISTING_CONDITION_LABELS[item.condition as ListingCondition]} ·{' '}
          {TRANSMISSION_LABELS[item.transmission as Transmission]}
        </p>
        <p className="mt-1 text-xs text-ink-500">
          {item.city_name} · {FUEL_LABELS[item.fuel as FuelType]}
        </p>

        {/* Le prix sur sa ligne : à 240 px, « 14 900 000 FCFA » et un bouton
            côte à côte forcent le montant à se couper en deux — et un prix
            coupé en deux se lit mal, c'est pourtant la donnée qui décide. */}
        <div className="mt-auto pt-4">
          <p className="text-[17px] font-extrabold leading-none text-ink-950">
            {formatAmount(item.price, item.currency)}
          </p>
          <p className="mt-1 text-xs text-ink-500">
            {item.is_negotiable ? 'Prix négociable' : 'Prix ferme'}
          </p>
          <Link
            to={ROUTES.listing(item.id)}
            className="mt-3 flex h-10 items-center justify-center rounded-lg bg-brand-500 text-[13px] font-bold text-white hover:bg-brand-600"
          >
            Voir l’annonce
          </Link>
        </div>
      </div>
    </article>
  );
}

export function ListingCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
      <div className="aspect-[4/3] animate-pulse bg-ink-100" />
      <div className="space-y-3 p-5">
        <div className="h-4 w-2/3 animate-pulse rounded bg-ink-100" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-ink-100" />
        <div className="h-8 w-full animate-pulse rounded bg-ink-100" />
      </div>
    </div>
  );
}
